import React, { useState, useEffect, FormEvent, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useAreasNav } from '../../contexts/AreasContext';
import { areasAPI, dashboardAPI, logsAPI, type DashboardSummary } from '../../utils/api';
import { getEventTitle } from '../../utils/modelLabels';
import { getViewedLogIds, markLogViewed } from '../../utils/viewedLogs';
import DetectionsStatsModal from '../../components/DetectionsStatsModal/DetectionsStatsModal';
import DetectionPhotoModal from '../../components/DetectionPhotoModal/DetectionPhotoModal';
import AddCameraModal from '../../components/AddCameraModal/AddCameraModal';
import { prefetchAuthenticatedImage } from '../../utils/useAuthenticatedBlobUrl';
import Loader from '../../components/Loader/Loader';
import { SkeletonCard } from '../../components/Skeleton/Skeleton';
import '../../components/Skeleton/Skeleton.css';
import './Dashboard.css';

interface Area {
  id: number;
  name: string;
  description: string | null;
  cameras_count?: number;
  cameras_online?: number;
  cameras_offline?: number;
}

function formatCamerasCount(count: number): string {
  const n = Math.abs(count);
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return `${n} камера`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${n} камеры`;
  return `${n} камер`;
}

interface RecentEvent {
  id: number;
  timestamp: string | null;
  model: string;
  area_id: number;
  area_name: string;
  camera_id: number;
  camera_name: string;
  screen_url?: string | null;
}

const RECENT_EVENTS_PAGE_SIZE = 3;
const DASHBOARD_EVENTS_POLL_MS = 15_000;

function formatEventsChange(percent: number): string {
  if (percent > 0) {
    return `+${percent}% по сравнению со вчера`;
  }
  if (percent < 0) {
    return `${percent}% по сравнению со вчера`;
  }
  return 'без изменений по сравнению со вчера';
}

function formatEventTime(timestamp: string | null): string {
  if (!timestamp) return '[ — ]';
  const d = new Date(timestamp.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return '[ — ]';
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  return `[ ${hh}:${mm}:${ss} ]`;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAdmin } = useAuth();
  const { requireSubscription } = useSubscription();
  const { refreshAreas } = useAreasNav();
  const [areas, setAreas] = useState<Area[]>([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [recentEvents, setRecentEvents] = useState<RecentEvent[]>([]);
  const [recentEventsLoading, setRecentEventsLoading] = useState(true);
  const [recentEventsHasMore, setRecentEventsHasMore] = useState(false);
  const [recentEventsLoadingMore, setRecentEventsLoadingMore] = useState(false);
  const [viewedLogIds, setViewedLogIds] = useState<Set<number>>(() => getViewedLogIds());
  const [isAllAreasStatsOpen, setIsAllAreasStatsOpen] = useState(false);
  const [isDetectionModalOpen, setIsDetectionModalOpen] = useState(false);
  const [detectionPhotoUrl, setDetectionPhotoUrl] = useState<string | null>(null);
  const [detectionModalLoading, setDetectionModalLoading] = useState(false);
  const [detectionModalError, setDetectionModalError] = useState<string | null>(null);
  const [isAddCameraModalOpen, setIsAddCameraModalOpen] = useState(false);
  const [openAreaMenuId, setOpenAreaMenuId] = useState<number | null>(null);
  const areaMenuRef = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string>('');
  
  // Форма создания
  const [areaName, setAreaName] = useState('');
  const [areaDescription, setAreaDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Модальное окно редактирования
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingArea, setEditingArea] = useState<Area | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  
  // Модальное окно удаления
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingArea, setDeletingArea] = useState<Area | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const recentEventsLimitRef = useRef(RECENT_EVENTS_PAGE_SIZE);

  // Загрузка участков и сводки
  useEffect(() => {
    setAreas([]);
    setRecentEvents([]);
    setSummary(null);
    loadAreas();
    void loadSummary();
    void loadRecentEvents({ sync: true });
  }, [user?.id]);

  useEffect(() => {
    if (openAreaMenuId == null) return;

    const handleOutside = (e: MouseEvent) => {
      if (areaMenuRef.current && !areaMenuRef.current.contains(e.target as Node)) {
        setOpenAreaMenuId(null);
      }
    };

    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [openAreaMenuId]);

  useEffect(() => {
    if (!isDeleteModalOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isDeleting) {
        setIsDeleteModalOpen(false);
        setDeletingArea(null);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isDeleteModalOpen, isDeleting]);

  // Переход из колокольчика «Все события» → прокрутка к ленте
  useEffect(() => {
    if (location.hash !== '#events') return;

    const timer = window.setTimeout(() => {
      document
        .getElementById('dashboard-events')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);

    return () => window.clearTimeout(timer);
  }, [location.hash, location.key]);

  const loadSummary = useCallback(async (options?: { silent?: boolean }) => {
    try {
      if (!options?.silent) {
        setSummaryLoading(true);
      }
      const response = await dashboardAPI.getSummary();
      if (response.success) {
        setSummary({
          cameras: response.cameras,
          events: response.events,
        });
      }
    } catch {
      if (!options?.silent) {
        setSummary(null);
      }
    } finally {
      if (!options?.silent) {
        setSummaryLoading(false);
      }
    }
  }, []);

  const loadRecentEvents = useCallback(async (options?: { silent?: boolean; sync?: boolean }) => {
    try {
      if (!options?.silent) {
        setRecentEventsLoading(true);
      }
      const limit = Math.max(RECENT_EVENTS_PAGE_SIZE, recentEventsLimitRef.current);
      const response = await dashboardAPI.getRecentEvents({
        limit,
        offset: 0,
        sync: options?.sync,
      });
      if (response.success) {
        setRecentEvents(response.events);
        setRecentEventsHasMore(response.has_more);
        recentEventsLimitRef.current = Math.max(recentEventsLimitRef.current, response.events.length);
        response.events.forEach((event) => {
          void prefetchAuthenticatedImage(event.screen_url);
        });
      }
    } catch {
      if (!options?.silent) {
        setRecentEvents([]);
        setRecentEventsHasMore(false);
      }
    } finally {
      if (!options?.silent) {
        setRecentEventsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    const refreshFeed = () => {
      void loadRecentEvents({ silent: true, sync: true });
      void loadSummary({ silent: true });
    };

    const intervalId = window.setInterval(refreshFeed, DASHBOARD_EVENTS_POLL_MS);

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        refreshFeed();
      }
    };

    document.addEventListener('visibilitychange', onVisible);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [loadRecentEvents, loadSummary]);

  const loadMoreRecentEvents = async () => {
    try {
      setRecentEventsLoadingMore(true);
      const response = await dashboardAPI.getRecentEvents({
        limit: RECENT_EVENTS_PAGE_SIZE,
        offset: recentEvents.length,
      });
      if (response.success) {
        setRecentEvents((prev) => {
          const next = [...prev, ...response.events];
          recentEventsLimitRef.current = next.length;
          return next;
        });
        setRecentEventsHasMore(response.has_more);
        response.events.forEach((event) => {
          void prefetchAuthenticatedImage(event.screen_url);
        });
      }
    } finally {
      setRecentEventsLoadingMore(false);
    }
  };

  const openDetectionModal = async (event: RecentEvent) => {
    setDetectionModalError(null);
    setIsDetectionModalOpen(true);

    if (event.screen_url) {
      setDetectionPhotoUrl(event.screen_url);
      setDetectionModalLoading(false);
      void prefetchAuthenticatedImage(event.screen_url);
      return;
    }

    setDetectionPhotoUrl(null);
    setDetectionModalLoading(true);

    try {
      const response = await logsAPI.getById(event.area_id, event.id);
      if (response.success && response.log?.screen_url) {
        setDetectionPhotoUrl(response.log.screen_url);
        void prefetchAuthenticatedImage(response.log.screen_url);
      } else {
        setDetectionModalError('Фотофиксация для этого события недоступна');
      }
    } catch {
      setDetectionModalError('Не удалось загрузить событие');
    } finally {
      setDetectionModalLoading(false);
    }
  };

  const closeDetectionModal = () => {
    setIsDetectionModalOpen(false);
    setDetectionPhotoUrl(null);
    setDetectionModalError(null);
    setDetectionModalLoading(false);
  };

  const handleOpenEvent = useCallback((event: RecentEvent) => {
    markLogViewed(event.id);
    setViewedLogIds(getViewedLogIds());
    requireSubscription(() => {
      void openDetectionModal(event);
    });
  }, [requireSubscription]);

  const openAddCameraModal = () => {
    requireSubscription(() => setIsAddCameraModalOpen(true));
  };

  const openFirstArea = useCallback(() => {
    if (areas.length === 0) {
      setError('Сначала создайте участок');
      return;
    }
    requireSubscription(() => {
      navigate(`/area/${areas[0].id}`);
    });
  }, [areas, navigate, requireSubscription]);

  const openReportsModal = () => {
    requireSubscription(() => setIsAllAreasStatsOpen(true));
  };

  const handleAllEvents = () => {
    const areaId = recentEvents[0]?.area_id ?? areas[0]?.id;
    if (!areaId) {
      setError('Сначала создайте участок');
      return;
    }
    requireSubscription(() => navigate(`/area/${areaId}`));
  };

  const loadAreas = async () => {
    try {
      setLoading(true);
      const response = await areasAPI.getAll();
      if (response.success) {
        setAreas(response.areas);
      }
    } catch (err: any) {
      setError(err.message || 'Ошибка загрузки участков');
    } finally {
      setLoading(false);
    }
  };

  // Создание участка
  const handleCreateArea = async (e: FormEvent) => {
    e.preventDefault();
    if (!areaName.trim()) {
      setError('Введите название участка');
      return;
    }

    try {
      setIsSubmitting(true);
      setError('');
      const response = await areasAPI.create(areaName.trim(), areaDescription.trim() || undefined);
      if (response.success) {
        setAreaName('');
        setAreaDescription('');
        await loadAreas();
        void refreshAreas();
      }
    } catch (err: any) {
      setError(err.message || 'Ошибка создания участка');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Открытие модального окна редактирования
  const handleEditClick = (area: Area) => {
    setEditingArea(area);
    setEditName(area.name);
    setEditDescription(area.description || '');
    setIsEditModalOpen(true);
  };

  // Сохранение изменений
  const handleUpdateArea = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingArea || !editName.trim()) {
      setError('Введите название участка');
      return;
    }

    try {
      setIsSubmitting(true);
      setError('');
      const response = await areasAPI.update(
        editingArea.id,
        editName.trim(),
        editDescription.trim() || undefined
      );
      if (response.success) {
        setIsEditModalOpen(false);
        setEditingArea(null);
        await loadAreas();
        void refreshAreas();
      }
    } catch (err: any) {
      setError(err.message || 'Ошибка обновления участка');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Открытие модального окна удаления
  const handleDeleteClick = (area: Area) => {
    requireSubscription(() => {
      setError('');
      setDeletingArea(area);
      setIsDeleteModalOpen(true);
    });
  };

  // Подтверждение удаления участка
  const handleConfirmDelete = async () => {
    if (!deletingArea) return;

    try {
      setIsDeleting(true);
      setError('');
      const response = await areasAPI.delete(deletingArea.id);
      if (response.success) {
        setIsDeleteModalOpen(false);
        setDeletingArea(null);
        await loadAreas();
        void refreshAreas();
      }
    } catch (err: any) {
      setError(err.message || 'Ошибка удаления участка');
    } finally {
      setIsDeleting(false);
    }
  };

  const totalCameras = summary?.cameras.total ?? 0;
  const onlineCameras = summary?.cameras.online ?? 0;
  const offlineCameras = Math.max(0, totalCameras - onlineCameras);

  return (
    <div className="dashboard">
      <div className="container ui-page">
        <section className="dashboard-welcome">
          <div className="ui-page-head">
            <div className="ui-page-head__text">
              <h1 className="dashboard-welcome__title">
                <span className="dashboard-welcome-title-span">Добро пожаловать,</span>{' '}
                {user ? (
                  user.name
                ) : (
                  <span className="dashboard-welcome__name-skeleton">
                    <span className="ui-skeleton" style={{ display: 'inline-block', width: '160px', height: '28px' }} />
                  </span>
                )}
              </h1>
              <p className="dashboard-welcome__text">
                Создайте участок, добавляйте камеры, выбирайте необходимые аналитики,
                отслеживайте результаты
              </p>
            </div>

            <div className="ui-page-head__actions">
              <button type="button" className="ui-btn ui-btn--outline" onClick={openReportsModal}>
                Отчёты
              </button>
              <button type="button" className="ui-btn ui-btn--primary" onClick={openAddCameraModal}>
                <span aria-hidden="true">+</span>
                Добавить камеру
              </button>
            </div>
          </div>

          <div className="dashboard-stats">
            <article className="dashboard-stat-card">
              <span className="ui-icon-tile" aria-hidden="true">
                <img
                  src={`${process.env.PUBLIC_URL}/icons/svg_icon/camera.svg`}
                  alt=""
                  className="dashboard-stat-card__icon"
                />
              </span>
              <div className="dashboard-stat-card__body">
                <span className="dashboard-stat-card__label">Камер онлайн</span>
                {summaryLoading ? (
                  <div className="dashboard-stat-card__value-skeleton ui-skeleton" />
                ) : (
                  <strong className="dashboard-stat-card__value">
                    {summary ? `${summary.cameras.online}/${summary.cameras.total}` : '—'}
                  </strong>
                )}
                <span className="dashboard-stat-card__hint">
                  {summaryLoading
                    ? 'Загрузка…'
                    : summary && summary.cameras.total > 0
                      ? `${summary.cameras.online_percent}% от всех камер`
                      : 'Нет камер'}
                </span>
              </div>
            </article>

            <article className="dashboard-stat-card">
              <span className="ui-icon-tile" aria-hidden="true">
                <svg className="dashboard-stat-card__icon dashboard-stat-card__icon--chart" viewBox="0 0 24 24" fill="none">
                  <path d="M4 19V5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <path d="M4 19H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <path d="M8 15V11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <path d="M12 15V8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <path d="M16 15V13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </span>
              <div className="dashboard-stat-card__body">
                <span className="dashboard-stat-card__label">События за сегодня</span>
                {summaryLoading ? (
                  <div className="dashboard-stat-card__value-skeleton ui-skeleton" />
                ) : (
                  <strong className="dashboard-stat-card__value">
                    {summary ? summary.events.today : '—'}
                  </strong>
                )}
                <span
                  className={`dashboard-stat-card__hint${
                    summary && summary.events.change_percent > 0
                      ? ' dashboard-stat-card__hint--up'
                      : summary && summary.events.change_percent < 0
                        ? ' dashboard-stat-card__hint--down'
                        : ''
                  }`}
                >
                  {summaryLoading
                    ? 'Загрузка…'
                    : summary
                      ? formatEventsChange(summary.events.change_percent)
                      : '—'}
                </span>
              </div>
            </article>

            <article className="dashboard-stat-card">
              <span className="ui-icon-tile ui-icon-tile--muted" aria-hidden="true">
                <img
                  src={`${process.env.PUBLIC_URL}/icons/svg_icon/roi.svg`}
                  alt=""
                  className="dashboard-stat-card__icon"
                />
              </span>
              <div className="dashboard-stat-card__body">
                <span className="dashboard-stat-card__label">Участков</span>
                {loading ? (
                  <div className="dashboard-stat-card__value-skeleton ui-skeleton" />
                ) : (
                  <strong className="dashboard-stat-card__value">{areas.length}</strong>
                )}
                <span className="dashboard-stat-card__hint">
                  {loading ? 'Загрузка…' : 'Групп камер в системе'}
                </span>
              </div>
            </article>

            <article className="dashboard-stat-card">
              <span
                className={`ui-icon-tile ${
                  offlineCameras > 0 ? 'ui-icon-tile--danger' : 'ui-icon-tile--success'
                }`}
                aria-hidden="true"
              >
                <img
                  src={`${process.env.PUBLIC_URL}/icons/svg_icon/camera.svg`}
                  alt=""
                  className="dashboard-stat-card__icon"
                />
              </span>
              <div className="dashboard-stat-card__body">
                <span className="dashboard-stat-card__label">Камер оффлайн</span>
                {summaryLoading ? (
                  <div className="dashboard-stat-card__value-skeleton ui-skeleton" />
                ) : (
                  <strong className="dashboard-stat-card__value">
                    {summary ? offlineCameras : '—'}
                  </strong>
                )}
                <span className="dashboard-stat-card__hint">
                  {summaryLoading
                    ? 'Загрузка…'
                    : offlineCameras > 0
                      ? 'Требуют внимания'
                      : 'Все камеры на связи'}
                </span>
              </div>
            </article>
          </div>
        </section>

        {error && (
          <div className="ui-alert ui-alert--danger" role="alert">
            {error}
          </div>
        )}

        <section className="dashboard-panels">
          <article
            id="dashboard-events"
            className="dashboard-panel dashboard-panel--events"
          >
            <header className="dashboard-panel__header">
              <h2 className="dashboard-panel__title">Последние события</h2>
              <button type="button" className="dashboard-panel__link" onClick={handleAllEvents}>
                Все события
              </button>
            </header>

            {recentEventsLoading ? (
              <div className="ui-loading">Загрузка событий…</div>
            ) : recentEvents.length === 0 ? (
              <div className="ui-empty">
                <span className="ui-empty__title">Событий пока нет</span>
                <span>Детекции появятся здесь сразу после срабатывания аналитик</span>
              </div>
            ) : (
              <ul className="dashboard-events-list">
                {recentEvents.map((event) => {
                  const isViewed = viewedLogIds.has(event.id);
                  return (
                    <li key={event.id}>
                      <button
                        type="button"
                        className="dashboard-event-row"
                        onClick={() => handleOpenEvent(event)}
                        onMouseEnter={() => void prefetchAuthenticatedImage(event.screen_url)}
                      >
                        <span className="ui-icon-tile dashboard-event-row__icon-wrap" aria-hidden="true">
                          <img
                            src={`${process.env.PUBLIC_URL}/icons/modals/people.svg`}
                            alt=""
                            className="dashboard-event-row__icon"
                          />
                        </span>
                        <span className="dashboard-event-row__time">{formatEventTime(event.timestamp)}</span>
                        <span className="dashboard-event-row__content">
                          <span className="dashboard-event-row__title">{getEventTitle(event.model)}</span>
                          <span className="dashboard-event-row__meta">
                            {event.area_name} • {event.camera_name}
                          </span>
                        </span>
                        <span
                          className={`ui-badge dashboard-event-row__badge ${
                            isViewed ? 'dashboard-event-row__badge--viewed' : 'ui-badge--accent dashboard-event-row__badge--new'
                          }`}
                        >
                          {isViewed ? 'Просмотрено' : 'Новое'}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}

            {recentEventsHasMore && !recentEventsLoading && (
              <button
                type="button"
                className="dashboard-panel__more"
                onClick={loadMoreRecentEvents}
                disabled={recentEventsLoadingMore}
              >
                {recentEventsLoadingMore ? 'Загрузка…' : (
                  <>
                    Показать еще
                    <svg
                      className="dashboard-panel__more-chevron"
                      viewBox="0 0 24 24"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M6 9L12 15L18 9"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </>
                )}
              </button>
            )}
          </article>

          <article className="dashboard-panel dashboard-panel--actions">
            <header className="dashboard-panel__header">
              <h2 className="dashboard-panel__title">Быстрые действия</h2>
            </header>
            <ul className="dashboard-actions-list">
              <li>
                <button type="button" className="dashboard-action-row" onClick={openAddCameraModal}>
                  <span className="ui-icon-tile dashboard-action-row__icon-wrap" aria-hidden="true">
                    <img src={`${process.env.PUBLIC_URL}/icons/svg_icon/camera.svg`} alt="" />
                  </span>
                  <span className="dashboard-action-row__text">
                    <span className="dashboard-action-row__title">Добавить камеру</span>
                    <span className="dashboard-action-row__desc">Подключить камеру к системе</span>
                  </span>
                  <span className="dashboard-action-row__chevron" aria-hidden="true">›</span>
                </button>
              </li>
              <li>
                <button type="button" className="dashboard-action-row" onClick={() => openFirstArea()}>
                  <span className="ui-icon-tile dashboard-action-row__icon-wrap" aria-hidden="true">
                    <img src={`${process.env.PUBLIC_URL}/icons/svg_icon/roi.svg`} alt="" />
                  </span>
                  <span className="dashboard-action-row__text">
                    <span className="dashboard-action-row__title">Добавить аналитику</span>
                    <span className="dashboard-action-row__desc">Настроить детекцию</span>
                  </span>
                  <span className="dashboard-action-row__chevron" aria-hidden="true">›</span>
                </button>
              </li>
              <li>
                <button
                  type="button"
                  className="dashboard-action-row"
                  onClick={openReportsModal}
                >
                  <span className="ui-icon-tile dashboard-action-row__icon-wrap" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" className="dashboard-action-row__icon-svg">
                      <path d="M4 19V5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      <path d="M4 19H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      <path d="M8 15V11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      <path d="M12 15V8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      <path d="M16 15V13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  </span>
                  <span className="dashboard-action-row__text">
                    <span className="dashboard-action-row__title">Посмотреть отчеты</span>
                    <span className="dashboard-action-row__desc">Аналитика и статистика</span>
                  </span>
                  <span className="dashboard-action-row__chevron" aria-hidden="true">›</span>
                </button>
              </li>
              <li>
                <button
                  type="button"
                  className="dashboard-action-row"
                  onClick={() => (isAdmin ? navigate('/admin') : navigate('/profile'))}
                >
                  <span className="ui-icon-tile dashboard-action-row__icon-wrap" aria-hidden="true">
                    <img src={`${process.env.PUBLIC_URL}/icons/header/user.svg`} alt="" />
                  </span>
                  <span className="dashboard-action-row__text">
                    <span className="dashboard-action-row__title">Управление пользователями</span>
                    <span className="dashboard-action-row__desc">Доступ к системе</span>
                  </span>
                  <span className="dashboard-action-row__chevron" aria-hidden="true">›</span>
                </button>
              </li>
            </ul>
          </article>
        </section>

        <section className="dashboard-card">
          <header className="dashboard-card__head">
            <h2 className="dashboard-card__title">Создать участок группы камер</h2>
          </header>

          <form className="dashboard-card__form" onSubmit={handleCreateArea}>
            <div className="dashboard-card__field">
              <label className="ui-label" htmlFor="area-name">Название участка</label>
              <input
                id="area-name"
                type="text"
                className="ui-input dashboard-input"
                placeholder="Например, Склад №1"
                value={areaName}
                onChange={(e) => setAreaName(e.target.value)}
                required
              />
            </div>

            <div className="dashboard-card__field">
              <label className="ui-label" htmlFor="area-description">Описание</label>
              <input
                id="area-description"
                type="text"
                className="ui-input dashboard-input"
                placeholder="Необязательно"
                value={areaDescription}
                onChange={(e) => setAreaDescription(e.target.value)}
              />
            </div>

            <button type="submit" className="ui-btn ui-btn--dark dashboard-button" disabled={isSubmitting}>
              <span className="dashboard-button__icon">+</span>
              <span>{isSubmitting ? 'Создание...' : 'Добавить участок'}</span>
            </button>
          </form>
        </section>

        <section className="dashboard-areas">
          <header className="dashboard-areas__head">
            <h2 className="dashboard-areas__title">Участки с камерами</h2>
            {!loading && areas.length > 0 && (
              <span className="ui-badge">{formatCamerasCount(totalCameras)}</span>
            )}
          </header>

          {loading ? (
            <div className="dashboard-areas__grid">
              {Array.from({ length: 4 }).map((_, index) => (
                <SkeletonCard key={index} />
              ))}
            </div>
          ) : areas.length === 0 ? (
            <div className="ui-card ui-empty">
              <span className="ui-empty__title">Участки не найдены</span>
              <span>Создайте первый участок в форме выше — и добавьте в него камеры</span>
            </div>
          ) : (
            <div className="dashboard-areas__grid">
              {areas.map((area) => {
                const total = area.cameras_count ?? 0;
                const online = area.cameras_online ?? 0;
                const offline = area.cameras_offline ?? Math.max(0, total - online);

                return (
                  <article key={area.id} className="area-card ui-card ui-card--interactive">
                    <header className="area-card__header">
                      <h3 className="area-card__title">{area.name}</h3>
                      <div
                        className="area-card__menu-wrap"
                        ref={openAreaMenuId === area.id ? areaMenuRef : null}
                      >
                        <button
                          type="button"
                          className="area-card__menu-btn"
                          aria-label="Действия с участком"
                          aria-expanded={openAreaMenuId === area.id}
                          onClick={() =>
                            setOpenAreaMenuId((prev) => (prev === area.id ? null : area.id))
                          }
                        >
                          <span className="area-card__menu-dots" aria-hidden="true">
                            <span />
                            <span />
                            <span />
                          </span>
                        </button>
                        {openAreaMenuId === area.id && (
                          <div className="ui-menu area-card__menu" role="menu">
                            <button
                              type="button"
                              className="ui-menu__item"
                              role="menuitem"
                              onClick={() => {
                                setOpenAreaMenuId(null);
                                handleEditClick(area);
                              }}
                            >
                              Редактировать
                            </button>
                            <button
                              type="button"
                              className="ui-menu__item ui-menu__item--danger"
                              role="menuitem"
                              onClick={() => {
                                setOpenAreaMenuId(null);
                                handleDeleteClick(area);
                              }}
                            >
                              Удалить
                            </button>
                          </div>
                        )}
                      </div>
                    </header>

                    {area.description && (
                      <p className="area-card__desc">{area.description}</p>
                    )}

                    <div className="area-card__stats">
                      <p className="area-card__cameras-total">{formatCamerasCount(total)}</p>
                      <p className="area-card__cameras-status">
                        <span className="area-card__status-online">
                          <span className="ui-dot" aria-hidden="true" />
                          {online} онлайн
                        </span>
                        <span className="area-card__status-offline">
                          <span className="ui-dot" aria-hidden="true" />
                          {offline} оффлайн
                        </span>
                      </p>
                    </div>

                    <button
                      className="ui-btn area-card__open"
                      type="button"
                      onClick={() => requireSubscription(() => navigate(`/area/${area.id}`))}
                    >
                      Открыть
                    </button>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </div>

      {/* Модальное окно редактирования */}
      {isEditModalOpen && editingArea && (
        <div className="ui-modal" role="dialog" aria-modal="true" aria-labelledby="edit-area-title">
          <button
            type="button"
            className="ui-modal__backdrop"
            aria-label="Закрыть"
            onClick={() => setIsEditModalOpen(false)}
          />
          <div className="ui-modal__dialog">
            <header className="ui-modal__header">
              <h2 id="edit-area-title" className="ui-modal__title">Редактировать участок</h2>
              <button
                type="button"
                className="ui-modal__close"
                aria-label="Закрыть"
                onClick={() => setIsEditModalOpen(false)}
              >
                ×
              </button>
            </header>

            <form onSubmit={handleUpdateArea}>
              <div className="ui-modal__body">
                {error && (
                  <div className="ui-alert ui-alert--danger" role="alert">
                    {error}
                  </div>
                )}

                <div className="ui-field">
                  <label className="ui-label" htmlFor="edit-area-name">Название участка</label>
                  <input
                    id="edit-area-name"
                    type="text"
                    className="ui-input"
                    placeholder="Название участка"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    required
                  />
                </div>

                <div className="ui-field">
                  <label className="ui-label" htmlFor="edit-area-description">Описание</label>
                  <input
                    id="edit-area-description"
                    type="text"
                    className="ui-input"
                    placeholder="Описание"
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                  />
                </div>
              </div>

              <footer className="ui-modal__footer">
                <button
                  type="button"
                  className="ui-btn ui-btn--outline"
                  onClick={() => setIsEditModalOpen(false)}
                  disabled={isSubmitting}
                >
                  Отмена
                </button>
                <button type="submit" className="ui-btn ui-btn--primary" disabled={isSubmitting}>
                  {isSubmitting ? 'Сохранение...' : 'Сохранить'}
                </button>
              </footer>
            </form>
          </div>
        </div>
      )}

      {/* Модальное окно подтверждения удаления */}
      {isDeleteModalOpen && deletingArea && (
        <div
          className="ui-modal delete-confirm-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-confirm-title"
        >
          <button
            type="button"
            className="ui-modal__backdrop"
            aria-label="Закрыть"
            onClick={() => !isDeleting && setIsDeleteModalOpen(false)}
          />
          <div className="ui-modal__dialog ui-modal__dialog--sm">
            <header className="ui-modal__header">
              <h2 id="delete-confirm-title" className="ui-modal__title">
                Подтвердите действие
              </h2>
              <button
                type="button"
                className="ui-modal__close"
                aria-label="Закрыть"
                onClick={() => !isDeleting && setIsDeleteModalOpen(false)}
                disabled={isDeleting}
              >
                ×
              </button>
            </header>

            <div className="ui-modal__body">
              <p className="delete-confirm-modal__text">
                Вы уверены, что хотите удалить участок{' '}
                <strong>«{deletingArea.name}»</strong>?
              </p>

              <div className="ui-alert ui-alert--warning delete-confirm-modal__alert" role="alert">
                <span>
                  <strong className="delete-confirm-modal__alert-title">Внимание. </strong>
                  Все камеры этого участка будут удалены безвозвратно.
                </span>
              </div>

              <p className="delete-confirm-modal__hint">Это действие нельзя отменить.</p>

              {error && (
                <div className="ui-alert ui-alert--danger" role="alert">
                  {error}
                </div>
              )}
            </div>

            <footer className="ui-modal__footer">
              <button
                type="button"
                className="ui-btn ui-btn--outline"
                onClick={() => setIsDeleteModalOpen(false)}
                disabled={isDeleting}
              >
                Отмена
              </button>
              <button
                type="button"
                className="ui-btn ui-btn--danger"
                onClick={handleConfirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? 'Удаление...' : 'Удалить'}
              </button>
            </footer>
          </div>
        </div>
      )}

      {isAddCameraModalOpen && (
        <AddCameraModal
          areas={areas}
          onClose={() => setIsAddCameraModalOpen(false)}
          onSuccess={() => {
            void loadAreas();
            void refreshAreas();
          }}
        />
      )}

      {isAllAreasStatsOpen && (
        <DetectionsStatsModal
          areaName="Все участки"
          onClose={() => setIsAllAreasStatsOpen(false)}
        />
      )}

      <DetectionPhotoModal
        isOpen={isDetectionModalOpen}
        photoUrl={detectionPhotoUrl}
        onClose={closeDetectionModal}
        fetchLoading={detectionModalLoading}
        fetchError={detectionModalError}
      />
    </div>
  );
};

export default Dashboard;
